import { DOCUMENT } from '@angular/common';
import { inject, Inject, Injectable } from '@angular/core';
import { ModalController, NavController } from '@ionic/angular';
import { TranslocoService } from '@jsverse/transloco';
import { AccountRepository } from '@timechef/features/account/state';
import { environment } from 'src/environments/environment';
import { ILogger, LoggerService } from '../logging';
import { RgpdCookieConfigModel } from '../models/rgpd-cookie-config.model';

let orejimeConfig: any;
@Injectable({
  providedIn: 'root',
})
export class RgpdCookiesService {
  logger: ILogger;
  window: any;

  checkIfOrejimeIsVisibleInterval: ReturnType<typeof setTimeout> | null = null;

  modalCtrl = inject(ModalController);

  constructor(
    @Inject(DOCUMENT) document: Document,
    private accountRepo: AccountRepository,
    private navCtrl: NavController,
    loggerService: LoggerService,
    private transloco: TranslocoService,
  ) {
    this.window = document.defaultView;
    this.logger = loggerService.getLogger('RgpdCookiesService');
  }

  public prompt(params: { isInitialPrompt: boolean }) {
    // if previous timer is still on, clear it
    if (this.checkIfOrejimeIsVisibleInterval) clearInterval(this.checkIfOrejimeIsVisibleInterval);

    if (params.isInitialPrompt) this.checkGoogleAnalyticsConsent();

    orejimeConfig = this.getOrejimeConfig();
    if (!this.window.Orejime) {
      this.logger.error('Orejime is NULL!');
      return;
    }

    // Start listening -> a.orejime-Modal-privacyPolicyLink
    const privacyPolicyLinkEventListener = (ev: any) => {
      if (ev?.target && ev.target.classList.contains('orejime-Modal-privacyPolicyLink')) {
        // Cancel everything and redirect with Angular/Ionic Router
        ev.preventDefault();
        document.removeEventListener('click', privacyPolicyLinkEventListener);
        debugger;
        this.navCtrl.navigateForward('/infos?tab=cgu');
        this.modalCtrl.dismiss();
      }
    };

    document.addEventListener('click', privacyPolicyLinkEventListener);

    this.window.orejimeInstance = this.window.Orejime.init(orejimeConfig);
    if (!params.isInitialPrompt) this.window.orejimeInstance.show();

    // Start Timer to check when we remove the event listener
    this.checkIfOrejimeIsVisibleInterval = setInterval(() => {
      const { react } = this.window.orejimeInstance.internals;

      // Consent div at the bottom
      const isBaseOpen = react.base.childElementCount > 0;
      // Details modal
      const isModalOpen = react.state.isModalVisible;
      // If neither is open, we can stop listening
      if (!isBaseOpen && !isModalOpen) {
        if (this.checkIfOrejimeIsVisibleInterval) clearInterval(this.checkIfOrejimeIsVisibleInterval);
        document.removeEventListener('click', privacyPolicyLinkEventListener);
      }
    }, 1000);
  }

  private getOrejimeConfig() {
    const lang = this.accountRepo.getLanguage()!;
    const config: RgpdCookieConfigModel = {
      applicationName: 'Timechef',
      domain: this.window.location.hostname,
      appElementContainer: 'ion-content',
      lang: lang,
      expiresAfterDays: 180,
    };

    return {
      elementID: 'orejime',
      appElement: config.appElementContainer,
      cookieName: RGPD_COOKIE_NAME,
      cookieExpiresAfterDays: config.expiresAfterDays,
      cookieDomain: config.domain,
      stringifyCookie: (contents: any) => JSON.stringify(contents),
      parseCookie: (cookie: string) => JSON.parse(cookie),
      privacyPolicy: '/infos',
      default: true,
      mustConsent: false,
      mustNotice: false,
      lang: config.lang,
      logo: false,
      debug: false,
      translations: {
        [lang]: this.getTranslations(lang),
      },
      apps: [
        {
          name: 'google-analytics',
          title: 'Google Analytics',
          cookies: ['_ga', '_gat', '_gid', '__utma', '__utmb', '__utmc', '__utmt', '__utmz', '_gat_gtag_GTM_UA', '_gat_GTM_UA'],
          purposes: ['analytics'],
          callback: (consent: string, app: { name: string }) => {
            this.window[`ga-disable-${environment.googleTagManager.gaTrackingId}`] = !consent;
            console.log('set consent for app ' + app.name + ': consent=' + consent);
          },
          required: false,
          optOut: false,
          default: true,
          onlyOnce: true,
        },
        {
          name: 'cookies-fonctionnels',
          title: 'Fonctionalités de base',
          purposes: ['fonctionnel', 'security'],
          cookies: ['cookies-fonctionnels'],
          required: true,
        },
      ],
      categories: [
        {
          name: 'fonctionnels',
          title: 'Cookies fonctionnels',
          apps: ['cookies-fonctionnels'],
        },
        {
          name: 'analytics',
          title: 'Cookies analytiques',
          apps: ['google-analytics'],
        },
      ],
    };
  }

  private getTranslations(lang: string) {
    return {
      consentModal: {
        title: this.transloco.translate('COOKIES.consentModal.title', {}, lang),
        description: this.transloco.translate('COOKIES.consentModal.description', { appName: 'Timechef' }, lang),
        privacyPolicy: {
          name: this.transloco.translate('COOKIES.consentModal.privacyPolicy.name', {}, lang),
          text: this.transloco.translate('COOKIES.consentModal.privacyPolicy.text', { privacyPolicy: '{privacyPolicy}' }, lang),
        },
      },
      consentNotice: {
        changeDescription: this.transloco.translate('COOKIES.consentNotice.changeDescription', {}, lang),
        description: this.transloco.translate('COOKIES.consentNotice.description', { appName: 'Timechef' }, lang),
        learnMore: this.transloco.translate('COOKIES.consentNotice.learnMore', {}, lang),
      },
      purposes: {
        analytics: this.transloco.translate('COOKIES.purposes.analytics', {}, lang),
        security: this.transloco.translate('COOKIES.purposes.security', {}, lang),
        fonctionnel: this.transloco.translate('COOKIES.purposes.fonctionnel', {}, lang),
      },
      categories: {
        analytics: {
          description: this.transloco.translate('COOKIES.categories.analytics.description', {}, lang),
        },
        fonctionnels: {
          description: this.transloco.translate('COOKIES.categories.fonctionnels.description', { appName: 'Timechef' }, lang),
        },
      },
      accept: this.transloco.translate('COOKIES.accept', {}, lang),
      acceptTitle: this.transloco.translate('COOKIES.acceptTitle', {}, lang),
      acceptAll: this.transloco.translate('COOKIES.acceptAll', {}, lang),
      save: this.transloco.translate('COOKIES.save', {}, lang),
      saveData: this.transloco.translate('COOKIES.saveData', {}, lang),
      decline: this.transloco.translate('COOKIES.decline', {}, lang),
      declineAll: this.transloco.translate('COOKIES.declineAll', {}, lang),
      close: this.transloco.translate('COOKIES.close', {}, lang),
      enabled: this.transloco.translate('COOKIES.enabled', {}, lang),
      disabled: this.transloco.translate('COOKIES.disabled', {}, lang),
      app: {
        optOut: {
          title: this.transloco.translate('COOKIES.app.optOut.title', {}, lang),
          description: this.transloco.translate('COOKIES.app.optOut.description', {}, lang),
        },
        required: {
          title: this.transloco.translate('COOKIES.app.required.title', {}, lang),
          description: this.transloco.translate('COOKIES.app.required.description', {}, lang),
        },
        purposes: this.transloco.translate('COOKIES.app.purposes', {}, lang),
        purpose: this.transloco.translate('COOKIES.app.purpose', {}, lang),
      },
      poweredBy: '',
      newWindow: this.transloco.translate('COOKIES.newWindow', {}, lang),
    };
  }

  private checkGoogleAnalyticsConsent() {
    const rgpdCookie = this.getCookie(RGPD_COOKIE_NAME);
    const gaUser = environment.googleTagManager.gaTrackingId;
    const consent = rgpdCookie && rgpdCookie['google-analytics'] === true;
    this.window[`ga-disable-${gaUser}`] = !consent;
  }

  private getCookie(name: string): any {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) == 0) {
        return JSON.parse(c.substring(nameEQ.length, c.length));
      }
    }

    return null;
  }
}

const RGPD_COOKIE_NAME = 'RGPD_COOKIE';
